import React, { createContext, useContext, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { quizApi, type QuizGuessInput, type QuizResponseInput } from "lib/api";
import { getQuizUiStrings, resolveQuizLocale, type QuizUiStrings } from "i18n/quizUi";
import type {
  QuizDetail,
  QuizInviteResult,
  QuizIssueRanking,
  QuizResults,
  QuizSubmitTeabatePayload,
} from "types/quiz";
import { quizQueryKeys } from "./quizQueryKeys";
import { useQuizSocket } from "./useQuizSocket";

interface QuizProviderProps {
  code?: string | null;
  children: React.ReactNode;
}

interface QuizContextValue {
  code: string | null;
  ui: QuizUiStrings;
  quiz: QuizDetail | null;
  results: QuizResults | null;
  quizError: Error | null;
  resultsError: Error | null;
  isQuizLoading: boolean;
  isResultsLoading: boolean;
  isFetching: boolean;
  isRealtimeConnected: boolean;
  qrUrl: string | null;
  refreshQuiz: () => Promise<void>;
  sendInvites: (emails: string[]) => Promise<QuizInviteResult>;
  createQuiz: () => Promise<QuizDetail>;
  joinQuiz: () => Promise<QuizDetail>;
  startQuiz: () => Promise<QuizDetail>;
  submitIssueRankings: (rankings: QuizIssueRanking[]) => Promise<QuizDetail>;
  submitTeabate: (payload: QuizSubmitTeabatePayload) => Promise<QuizDetail>;
  startPart2: () => Promise<QuizDetail>;
  submitGuesses: (guesses: QuizGuessInput[]) => Promise<QuizDetail>;
  submitResponses: (responses: QuizResponseInput[]) => Promise<QuizDetail>;
  endQuiz: () => Promise<QuizDetail>;
  publish: () => Promise<QuizResults>;
  isCreating: boolean;
  isJoining: boolean;
  isStarting: boolean;
  isSavingIssues: boolean;
  isSavingTeabate: boolean;
  isStartingPart2: boolean;
  isSavingGuesses: boolean;
  isSavingResponses: boolean;
  isEnding: boolean;
  isPublishing: boolean;
  isSendingInvites: boolean;
  isBusy: boolean;
}

const QuizContext = createContext<QuizContextValue | undefined>(undefined);

/**
 * Central quiz query + mutation provider.
 */
export const QuizProvider: React.FC<QuizProviderProps> = ({ code, children }) => {
  const queryClient = useQueryClient();
  const normalizedCode = String(code || "").trim().toLowerCase() || null;
  const ui = useMemo(() => getQuizUiStrings(resolveQuizLocale()), []);

  const quizQuery = useQuery({
    queryKey: normalizedCode ? quizQueryKeys.detail(normalizedCode) : ["quiz", "detail", "empty"],
    queryFn: () => quizApi.getQuiz(normalizedCode as string),
    enabled: Boolean(normalizedCode),
  });

  const isParticipant = Boolean(quizQuery.data?.quiz.viewer_is_participant);
  const { isRealtimeConnected } = useQuizSocket({
    code: normalizedCode,
    enabled: Boolean(normalizedCode && isParticipant),
  });

  const resultsQuery = useQuery({
    queryKey: normalizedCode ? quizQueryKeys.results(normalizedCode) : ["quiz", "results", "empty"],
    queryFn: () => quizApi.getResults(normalizedCode as string),
    enabled: Boolean(
      normalizedCode
      && isParticipant
      && (
        quizQuery.data?.quiz.status === "ended"
        || quizQuery.data?.viewer?.progress.part2_complete
      )
    ),
  });

  const invalidateQuiz = async () => {
    if (!normalizedCode) {
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: quizQueryKeys.detail(normalizedCode) }),
      queryClient.invalidateQueries({ queryKey: quizQueryKeys.results(normalizedCode) }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: () => quizApi.createQuiz(),
  });

  const joinMutation = useMutation({
    mutationFn: () => quizApi.joinQuiz(normalizedCode as string),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const startMutation = useMutation({
    mutationFn: () => quizApi.startQuiz(normalizedCode as string),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const issuesMutation = useMutation({
    mutationFn: (rankings: QuizIssueRanking[]) => quizApi.submitIssueRankings(normalizedCode as string, rankings),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const teabateMutation = useMutation({
    mutationFn: (payload: QuizSubmitTeabatePayload) => quizApi.submitTeabate(normalizedCode as string, payload),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const startPart2Mutation = useMutation({
    mutationFn: () => quizApi.startPart2(normalizedCode as string),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const guessesMutation = useMutation({
    mutationFn: (guesses: QuizGuessInput[]) => quizApi.submitGuesses(normalizedCode as string, guesses),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const responsesMutation = useMutation({
    mutationFn: (responses: QuizResponseInput[]) => quizApi.submitResponses(normalizedCode as string, responses),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const endMutation = useMutation({
    mutationFn: () => quizApi.endQuiz(normalizedCode as string),
    onSuccess: async () => {
      await invalidateQuiz();
    },
  });

  const publishMutation = useMutation({
    mutationFn: () => quizApi.publish(normalizedCode as string),
    onSuccess: async (data) => {
      if (!normalizedCode) {
        return;
      }
      queryClient.setQueryData(quizQueryKeys.results(normalizedCode), data);
      await invalidateQuiz();
    },
  });

  const invitesMutation = useMutation({
    mutationFn: (emails: string[]) => quizApi.sendInvites(normalizedCode as string, emails),
  });

  const isBusy = [
    createMutation.isPending,
    joinMutation.isPending,
    startMutation.isPending,
    issuesMutation.isPending,
    teabateMutation.isPending,
    startPart2Mutation.isPending,
    guessesMutation.isPending,
    responsesMutation.isPending,
    endMutation.isPending,
    publishMutation.isPending,
    invitesMutation.isPending,
  ].some(Boolean);

  const value: QuizContextValue = {
    code: normalizedCode,
    ui,
    quiz: quizQuery.data ?? null,
    results: resultsQuery.data ?? null,
    quizError: (quizQuery.error as Error) ?? null,
    resultsError: (resultsQuery.error as Error) ?? null,
    isQuizLoading: quizQuery.isLoading,
    isResultsLoading: resultsQuery.isLoading,
    isFetching: quizQuery.isFetching || resultsQuery.isFetching,
    isRealtimeConnected,
    qrUrl: normalizedCode ? quizApi.getQuizQrUrl(normalizedCode) : null,
    refreshQuiz: async () => {
      await invalidateQuiz();
      await Promise.all([quizQuery.refetch(), resultsQuery.refetch()]);
    },
    sendInvites: (emails) => invitesMutation.mutateAsync(emails),
    createQuiz: () => createMutation.mutateAsync(),
    joinQuiz: () => joinMutation.mutateAsync(),
    startQuiz: () => startMutation.mutateAsync(),
    submitIssueRankings: (rankings) => issuesMutation.mutateAsync(rankings),
    submitTeabate: (payload) => teabateMutation.mutateAsync(payload),
    startPart2: () => startPart2Mutation.mutateAsync(),
    submitGuesses: (guesses) => guessesMutation.mutateAsync(guesses),
    submitResponses: (responses) => responsesMutation.mutateAsync(responses),
    endQuiz: () => endMutation.mutateAsync(),
    publish: () => publishMutation.mutateAsync(),
    isCreating: createMutation.isPending,
    isJoining: joinMutation.isPending,
    isStarting: startMutation.isPending,
    isSavingIssues: issuesMutation.isPending,
    isSavingTeabate: teabateMutation.isPending,
    isStartingPart2: startPart2Mutation.isPending,
    isSavingGuesses: guessesMutation.isPending,
    isSavingResponses: responsesMutation.isPending,
    isEnding: endMutation.isPending,
    isPublishing: publishMutation.isPending,
    isSendingInvites: invitesMutation.isPending,
    isBusy,
  };

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
};

export function useQuiz(): QuizContextValue {
  const context = useContext(QuizContext);
  if (!context) {
    throw new Error("useQuiz must be used within a QuizProvider");
  }
  return context;
}
